import { Checkbox, Form, FormProps, Radio } from 'antd';
import { FC } from 'react';

import { AppDatePicker } from '@/common/components';
import { AppFormProps, BaseQueryParams } from '@/common/interfaces';
import { defineInputStatus } from '@/common/utils';

import {
  PagesBulkPublishAction,
  PagesBulkPublishFormFields,
  PagesBulkPublishFormInputs,
  PagesBulkPublishScope,
  PageUsageForBots,
  PageUsageForKeys,
  PageUsageForType,
  useGetPagesBulkPublishListQuery,
} from '@/modules/Pages';

import { PagesBulkPublishPagesList } from './PagesBulkPublishPagesList';

const usageForOptions: { value: PageUsageForType; label: string }[] = [
  { value: { type: 'default' }, label: 'Звичайні сторінки' },
  { value: { [PageUsageForKeys.Bot]: PageUsageForBots.Google }, label: 'Сторінки для Google Bot' },
];

interface Props extends AppFormProps<typeof PagesBulkPublishFormInputs, PagesBulkPublishFormFields> {
  action: PagesBulkPublishAction;
  scope: PagesBulkPublishScope | undefined;
  usageForVariants: PageUsageForType[] | undefined;
  currentPage: number;
  pagesList: BaseQueryParams<typeof useGetPagesBulkPublishListQuery>['data'];
  isPagesListFetching: boolean;
  handleValuesChange: FormProps<PagesBulkPublishFormFields>['onValuesChange'];
  handleChangePage: (page: number) => void;
}

export const PagesBulkPublishModalView: FC<Props> = ({
  action,
  scope,
  usageForVariants,
  currentPage,
  pagesList,
  isPagesListFetching,
  formName,
  form,
  initialValues,
  formErrors,
  isFormLoading,
  validationSchema,
  handleFinish,
  handleFinishFailed,
  handleValuesChange,
  handleChangePage,
}) => {
  return (
    <Form
      form={form}
      name={formName}
      initialValues={initialValues}
      disabled={isFormLoading}
      autoComplete="off"
      onFinish={handleFinish}
      onFinishFailed={handleFinishFailed}
      onValuesChange={handleValuesChange}
    >
      <Form.Item label="Застосувати" name={PagesBulkPublishFormInputs.Scope} rules={validationSchema.scope}>
        <Radio.Group
          options={[
            { value: 'all', label: 'Для всіх' },
            { value: 'selected', label: 'Для вибраних' },
          ]}
          optionType="button"
          buttonStyle="solid"
        />
      </Form.Item>

      <Form.Item
        label="Типи сторінок"
        name={PagesBulkPublishFormInputs.UsageForVariants}
        help={formErrors?.usageForVariants}
        validateStatus={defineInputStatus(isFormLoading, formErrors?.usageForVariants)}
        rules={validationSchema.usageForVariants}
        getValueProps={(value?: PageUsageForType[]) => ({ value: value?.map((item) => JSON.stringify(item)) })}
        normalize={(value: string[]) => value.map((item) => JSON.parse(item) as PageUsageForType)}
      >
        <Checkbox.Group<string>
          options={usageForOptions.map(({ value, label }) => {
            const key = JSON.stringify(value);
            return {
              value: key,
              label,
              disabled: usageForVariants?.length === 1 && JSON.stringify(usageForVariants[0]) === key,
            };
          })}
        />
      </Form.Item>

      {action === 'publish' && (
        <Form.Item
          label="Дата публікації"
          hasFeedback
          name={PagesBulkPublishFormInputs.PublishedAt}
          help={formErrors?.publishedAt}
          validateStatus={defineInputStatus(isFormLoading, formErrors?.publishedAt)}
          rules={validationSchema.publishedAt}
          style={{ width: '100%' }}
        >
          <AppDatePicker showTime />
        </Form.Item>
      )}

      {scope === 'selected' && (
        <Form.Item
          name={PagesBulkPublishFormInputs.PageIds}
          help={formErrors?.pageIds}
          validateStatus={defineInputStatus(isFormLoading, formErrors?.pageIds)}
          rules={validationSchema.pageIds}
        >
          <PagesBulkPublishPagesList
            currentPage={currentPage}
            pagesListItems={pagesList.items}
            pagesListTotalItems={pagesList.meta.totalItems}
            pagesListItemsPerPage={pagesList.meta.itemsPerPage}
            isLoading={isPagesListFetching}
            disabled={isFormLoading}
            handleChangePage={handleChangePage}
          />
        </Form.Item>
      )}
    </Form>
  );
};
